import { Inject, Injectable, Optional } from '@angular/core';
import { TicketSendingOption } from '@core/data-access/models/shared/ticket-sending-options.model';
import { ScriptsAPI } from '@core/data-access/services/scripts/scripts-api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CUSTOMIZATION_DEBOUNCE_TIME } from '@shared/tokens';
import { environment } from '@src/environments/environment';
import {
  ActionTypes,
  GetExhibitionSettings,
  GetLocalizedImages,
  GetOperatorSettingWithEventEndpoint,
  GetOperatorsScripts,
  GetOperatorsSettings,
  GetRandomSponsor,
  SetExhibitionSettings,
  SetLocalizedImages,
  SetOperatorsScripts,
  SetOperatorsSettings,
  SetPartialOperatorSettingsWithEventEndpoint,
  SetRandomSponsor
} from '@store/customization/customization.actions';
import {
  ExhibitionSettingModel,
  LocalizedImagesModel,
  OperatorsSettingsModel,
  SponsorBannerModel
} from '@store/customization/customization.interfaces';
import { CustomizationService } from '@store/customization/customization.service';
import { PackageProductBookingActions } from '@store/products/booking/product/package/package-booking.actions';
import { ProductBookingActions } from '@store/products/booking/product/product-booking.actions';
import { VoucherProductBookingActions } from '@store/products/booking/product/voucher/voucher-booking.actions';
import { catchError, EMPTY, map, mergeMap, of, switchMap, tap, throwError } from 'rxjs';

@Injectable()
export class CustomizationEffect {
  loadCustomSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetExhibitionSettings>(ActionTypes.GET_EXHIBITION_SETTINGS),
      switchMap((data: any) => {
        const eventId = data.payload;
        if (eventId === '') {
          return EMPTY;
        }

        return this.customizationService.getCustomSettings(eventId).pipe(
          map((customSettings) => {
            const sendingOptions = Object.keys(customSettings.ticketSelectionDeliveryTypes).map((key) => {
              const isBasic =
                (key !== TicketSendingOption.MobilePerBuyer && key !== TicketSendingOption.TicketRetrievalLinkBuyer) ||
                false;
              const isBuyer =
                key === TicketSendingOption.MobilePerBuyer ||
                key === TicketSendingOption.TicketRetrievalLinkBuyer ||
                key === TicketSendingOption.AllToBuyer ||
                false;

              return {
                value: key,
                isEnabled: customSettings.ticketSelectionDeliveryTypes[key],
                isBasic,
                isBuyer
              };
            });

            // TODO add currency if missing in API
            if (!Object.hasOwn(customSettings, 'currency')) {
              customSettings.currency = environment.currency;
            }

            customSettings.ticketSelectionDeliveryTypes = sendingOptions;

            return new SetExhibitionSettings(customSettings);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetExhibitionSettings(null));
          })
        );
      })
    )
  );

  loadRandomSponsor$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetRandomSponsor>(ActionTypes.GET_RANDOM_SPONSOR),
      switchMap((data: any) => {
        const payload = data.payload;
        if (payload.eventId === '') {
          return EMPTY;
        }
        const { eventId, countryCode } = payload;
        return this.customizationService.getRandomSponsor(eventId, countryCode).pipe(
          map((randomSponsor: SponsorBannerModel) => new SetRandomSponsor(randomSponsor)),
          catchError((error) => {
            console.log(error);
            return of(new SetRandomSponsor(null));
          })
        );
      })
    )
  );

  loadLocalizedImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetLocalizedImages>(ActionTypes.GET_LOCALIZED_IMAGES),
      mergeMap((data: any) => {
        const { eventId, countryCode } = data.payload;
        return this.customizationService.getLocalizedImages(eventId, countryCode).pipe(
          map((localizedImages: LocalizedImagesModel) => {
            return new SetLocalizedImages(localizedImages);
          }),
          catchError((error) => {
            console.log(error);
            return of(new SetLocalizedImages(null));
          })
        );
      })
    )
  );

  loadOperatorSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetOperatorsSettings>(ActionTypes.GET_OPERATORS_SETTINGS),
      switchMap(() => {
        return this.customizationService.getOperatorSettings().pipe(
          map((operatorSettings: OperatorsSettingsModel) => new SetOperatorsSettings(operatorSettings)),
          catchError((error) => {
            console.log(error);
            return of(new SetOperatorsSettings(null));
          })
        );
      })
    )
  );

  getOperatorVisitorFormSettingsWithEventEndpoint$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetOperatorSettingWithEventEndpoint>(ActionTypes.GET_OPERATOR_SETTINGS_WITH_EVENT_ENDPOINT),
      switchMap(() =>
        this.customizationService.getCustomSettings(null).pipe(
          map(
            (operatorExhibitionSettings: ExhibitionSettingModel | null) =>
              new SetPartialOperatorSettingsWithEventEndpoint(operatorExhibitionSettings)
          ),
          catchError(() => of(new SetPartialOperatorSettingsWithEventEndpoint(null)))
        )
      )
    )
  );

  setShoppingStartTime$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProductBookingActions.setProductInBookingList,
          ProductBookingActions.removeBookedProductsAndSetPreferredProductInBookingList,
          VoucherProductBookingActions.setVoucherProductInBookingList,
          VoucherProductBookingActions.setVoucherForAnonymousProductInBookingList,
          PackageProductBookingActions.setPackageProductInBookingList
        ),
        tap(() => this.customizationService.setShoppingStartTime()),
        catchError((error) => throwError(() => error))
      ),
    { dispatch: false }
  );

  loadOperatorScripts$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetOperatorsScripts>(ActionTypes.GET_OPERATORS_SCRIPTS),
      switchMap(() => {
        return this.scriptsAPI.getOperatorScripts$().pipe(
          map((operatorScripts: string[]) => new SetOperatorsScripts(operatorScripts)),
          catchError((error) => {
            console.log(error);
            return of(new SetOperatorsScripts(null));
          })
        );
      })
    )
  );

  constructor(
    @Optional() @Inject(CUSTOMIZATION_DEBOUNCE_TIME) private customizationDebounceTime: number,
    private actions$: Actions,
    private customizationService: CustomizationService,
    private scriptsAPI: ScriptsAPI
  ) {}
}
